import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { readMenuAll } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { localize } from 'common/utils';

import { DiscountedItem, DiscountedItemCategory } from './useDiscountedItems';
import { useProductInformationSettings } from './useProductInformationSettings';

export const useMenuItems = (menuId: string) => {
  const { locale } = useIntl();

  const { isFetching: isLoadingProductInformationSetting, errorProductInformationSettings } =
    useProductInformationSettings(menuId);

  const { data, isFetching, error } = useQuery(['read-menu-all', menuId], () => readMenuAll(menuId), {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const isLoadingMenuData = isLoadingProductInformationSetting || isFetching;

  const { menuItems, menuItemCategories } = useMemo(() => {
    if (isLoadingMenuData || !data) {
      return { menuItems: [], menuItemCategories: [] };
    }

    const getItemsByCategoryRecursive = (categories: Category[]) =>
      categories.reduce<{ categories: DiscountedItemCategory[]; items: DiscountedItem[] }>(
        ({ categories, items }, category) => {
          const itemCategory = { id: category.id, name: localize(category.name, locale) };

          const categoryItemIds = category.item_bindings.reduce<string[]>((itemIds, { item_id }) => {
            const item = data.items.find(item => item.id === item_id);
            if (item) {
              itemIds.push(item_id);
              items.push({
                ...item,
                name: localize(item.name, locale),
                description: localize(item.description, locale),
              });
            }
            return itemIds;
          }, []);

          const { categories: childCategories, items: childItems } = getItemsByCategoryRecursive(
            category.child_categories,
          );

          if (categoryItemIds.length || childCategories.length) {
            categories.push({ ...itemCategory, itemIds: categoryItemIds, childCategories });
          }

          if (childItems.length) {
            items.push(...childItems);
          }

          return { categories, items };
        },
        { categories: [], items: [] },
      );

    const { categories, items } = getItemsByCategoryRecursive(data.menu.categories);

    return {
      menuItems: items,
      menuItemCategories: categories,
    };
  }, [isLoadingMenuData, data, locale]);

  return {
    menuItems,
    menuItemCategories,
    isLoadingMenuData,
    hasErrorMenuData: error || errorProductInformationSettings,
  };
};
